import { renderMIPCreateSectionHeaderPortal } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/mipCreatePortals';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { Tab, Tabs } from '@dealstack/frontend/shared/components/Tabs/Tabs';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { lazy, memo, Suspense } from 'react';
import styled from 'styled-components';

import { ForIndividuals } from './components/ForIndividuals';
import { ForVehicles } from './components/ForVehicles';
import {
	IndividualScheduleTabNames,
	useIndividualScheduleController,
} from './useIndividualScheduleController';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';

const PdfLoader = lazy(() =>
	import('@dealstack/frontend/shared/components/PdfLoader/PdfLoader').then((m) => ({
		default: m.PdfLoader,
	}))
);

interface Props {
	className?: string;
}

const IndividualSchedule = ({ className }: Props) => {
	const tMipCreate = useTranslationMipCreate();

	const controller = useIndividualScheduleController();
	return (
		<div className={className}>
			{renderMIPCreateSectionHeaderPortal(
				<SectionHeader
					title={tMipCreate('schedule.header.title')}
					subtitle={tMipCreate('schedule.header.subtitle')}
				/>
			)}

			<TabsWrapper>
				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_SCHEDULE_PREVIEW_INDIVIDUALS_TAB}
				>
					<Tab
						active={controller.isActiveTab(IndividualScheduleTabNames.INDIVIDUALS)}
						onClick={() => {
							controller.setSelectedTabName(IndividualScheduleTabNames.INDIVIDUALS);
						}}
					>
						{tMipCreate('schedule.tabs.individuals')}
					</Tab>
				</UIElementIdentifier>

				<UIElementIdentifier
					identifier={TEST_ELEMENT_IDS.MIP_CREATE_SCHEDULE_PREVIEW_PIV_TAB}
				>
					<Tab
						active={controller.isActiveTab(
							IndividualScheduleTabNames.PERSONAL_INVESTMENT_VEHICLES
						)}
						onClick={() => {
							controller.setSelectedTabName(
								IndividualScheduleTabNames.PERSONAL_INVESTMENT_VEHICLES
							);
						}}
					>
						{tMipCreate('schedule.tabs.personal_investment_vehicles')}
					</Tab>
				</UIElementIdentifier>
			</TabsWrapper>

			<GridSection>
				<ColumnWrapper gridAreaName="left">
					{controller.selectedTabName === IndividualScheduleTabNames.INDIVIDUALS ? (
						<ForIndividuals />
					) : (
						<ForVehicles />
					)}
				</ColumnWrapper>

				<ColumnWrapper gridAreaName="right">
					<SectionHeader
						size="h5b"
						title={tMipCreate(
							'schedule.securities_section.individual_schedule_preview'
						)}
					/>
					<PreviewWrapper>
						<Suspense fallback={<InlineLoader />}>
							<PdfLoader src={controller.pdfPath} pageHeight={780} pageWidth={540} />
						</Suspense>
					</PreviewWrapper>
				</ColumnWrapper>
			</GridSection>
		</div>
	);
};

const PreviewWrapper = styled.div`
	position: relative;
`;

const GridSection = styled.div`
	display: grid;
	grid-template-areas: 'left right';
	grid-column-gap: 3em;
	grid-template-columns: 1fr 1fr;
`;

const ColumnWrapper = styled.div<{ gridAreaName: string }>`
	.img {
		max-width: 100%;
		max-height: 100%;
	}

	display: flex;
	flex-direction: column;
	grid-area: ${(props) => props.gridAreaName};
	max-width: 42rem;
	margin-bottom: 1.5em;
`;
const TabsWrapper = styled(Tabs)`
	max-width: 22rem;
`;

const Memoized = memo(IndividualSchedule);
const Styled = styled(Memoized)`
	${TabsWrapper} {
		margin: 1.5em 0;
	}

	${PreviewWrapper} {
		margin-top: 1.5em;
		min-height: 48.75rem;
	}

	${PreviewWrapper} {
		${({ theme }) => theme.shadow.z2}
	}
`;

export { Styled as IndividualSchedule };
